import { useEffect, useRef, useState } from "react";
import { KiteBrand, KiteBrandService } from "../../services/KiteBrandService";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface BrandSearchInputProps {
  value: number | null;
  brandName?: string;
  onChange: (brandId: number, brandName: string) => void;
  required?: boolean;
  error?: string;
}

function BrandSearchInput({
  value,
  brandName,
  onChange,
  required = false,
  error,
}: BrandSearchInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<KiteBrand | null>(null);
  const [suggestions, setSuggestions] = useState<KiteBrand[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize with brandName if provided, or fetch it if only ID is available
  useEffect(() => {
    if (value) {
      if (brandName) {
        // If brandName is provided, use it directly without API call
        setSelectedBrand({ id: value, brand: brandName });
        setInputValue(brandName);
      } else {
        // Only fetch from API if brandName is not provided
        fetchBrandById(value);
      }
    } else {
      setInputValue("");
      setSelectedBrand(null);
    }
  }, [value, brandName]);

  // Handle clicks outside the component
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Function to fetch a brand by ID
  async function fetchBrandById(brandId: number) {
    try {
      const brand = await KiteBrandService.getBrandById(brandId);
      if (brand) {
        setSelectedBrand(brand);
        setInputValue(brand.brand);
      } else {
        console.warn(`Brand with ID ${brandId} not found`);
      }
    } catch (error) {
      console.error("Error fetching brand:", error);
    }
  }

  // Function to search brands with debounce
  function searchBrands(query: string) {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (query.length >= 3) {
      setIsLoading(true);

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const brands = await KiteBrandService.searchBrands(query, 20);
          setSuggestions(brands);
          setIsOpen(brands.length > 0);
        } catch (error) {
          console.error("Error searching brands:", error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Clear selected brand when input changes
    if (selectedBrand && newValue !== selectedBrand.brand) {
      setSelectedBrand(null);
      onChange(0, "");
    }

    searchBrands(newValue);
  }

  function handleSelectBrand(brand: KiteBrand) {
    setSelectedBrand(brand);
    setInputValue(brand.brand);
    setIsOpen(false);
    onChange(brand.id, brand.brand);
  }

  function handleClearInput() {
    setInputValue("");
    setSelectedBrand(null);
    setSuggestions([]);
    setIsOpen(false);
    onChange(0, "");
  }

  return (
    <div className="space-y-2" ref={containerRef}>
      <label
        htmlFor="brand"
        className="block text-sm font-medium text-gray-700"
      >
        Brand {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          id="brand"
          value={inputValue}
          onChange={handleInputChange}
          className={`block w-full rounded-md border px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
            error ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Search for a brand..."
          required={required}
        />

        {/* Clear button */}
        {inputValue && (
          <button
            type="button"
            onClick={handleClearInput}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-10">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
          </div>
        )}

        {/* Suggestions dropdown */}
        {isOpen && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto z-50">
            <ul className="py-1">
              {suggestions.map((brand) => (
                <li
                  key={brand.id}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => handleSelectBrand(brand)}
                >
                  {brand.brand}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {inputValue.length > 0 && inputValue.length < 3 && (
        <p className="text-yellow-500 text-xs">
          Type at least 3 characters to search
        </p>
      )}
    </div>
  );
}

export default BrandSearchInput;
